import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RefHandler } from 'react-popper';
import PointsIcon from '@wix/da-shared-react/pkg/Icons/Points';
import ProgressBar from '@wix/da-shared-react/pkg/ProgressBar';
import Flex from '@wix/da-shared-react/pkg/Flex';
import classnames from 'classnames';
import { PapiEditorText } from '@wix/da-types-papi';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import DonationViewer from '@wix/da-shared-react/pkg/DaEditorWrapper/Viewer/Donation';
import { isEditorTextEmpty } from '@wix/da-shared-react/pkg/DaEditorWrapper/utils';
import TextTooltipOnHover from '@wix/da-shared-react/pkg/Tooltip/TextTooltipOnHover';
import { BiEventType, BiLink } from '@wix/da-shared-react/pkg/biLogger';

import s from './Donations.scss';

export interface Props {
  className?: string;
  username: string;
  userUuid: string;
  title?: string;
  description?: string | PapiEditorText;
  goal?: number;
  amount?: number;
  donors?: number;
  isOwner: boolean;
  pushDonateModal: (username: string, title: string) => void;
  moduleId: number;
  moduleVersion: number;
}

const defaultProps = {
  donors: 0,
  amount: 0,
  goal: 100,
};

export class Donations extends React.Component<Props & WithTranslation> {
  static defaultProps = defaultProps;
  render() {
    const {
      className,
      title,
      description,
      goal,
      isOwner,
      amount,
      donors,
      moduleId,
      moduleVersion,
      t,
    } = this.props;

    let hasDescription = false;
    if (description) {
      hasDescription =
        typeof description === 'string'
          ? description.length > 0
          : !isEditorTextEmpty(description);
    }
    const percentage = goal ? (amount! / goal!) * 100 : 0;
    const hasReachedGoal = Boolean(goal && amount! >= goal!);
    return (
      <>
        <div
          className={classnames(
            s['root'],
            !hasDescription && s['no-description'],
            className
          )}
        >
          <div className={s['wrapper']}>
            {/* title and description */}
            {title && <div className={s['title']}>{title}</div>}
            {hasDescription && (
              <div className={s['description']}>
                {typeof description === 'string' ? (
                  { description }
                ) : (
                  <TextRenderer
                    textContent={description}
                    reactViewer={DonationViewer}
                    legacyTheme={'default'}
                    viewerKey={`donation-${moduleId}-${moduleVersion}`}
                  />
                )}
              </div>
            )}

            {/* goal */}
            <Flex className={s['section-title']} alignItems="center">
              <span>{goal}</span>
              <PointsIcon className={s['points-icon']} />
            </Flex>
            <div className={s['section-description']}>
              {t('widgets.donations.goal')}
            </div>

            {/* donors */}
            <div className={s['section-title']}>{donors}</div>
            <div className={s['section-description']}>
              {t('widgets.donations.donors')}
            </div>

            {/* status */}
            <div className={s['section-title']}>
              {t('widgets.donations.status')}
            </div>
            <div
              className={classnames(
                s['section-description'],
                s['section-description-status']
              )}
            >
              {t('widgets.donations.status.progress', {
                progress: `${amount}/${goal}`,
              })}
            </div>

            <ProgressBar
              percentage={percentage}
              className={s['progressbar-root']}
            />

            {hasReachedGoal && (
              <div className={s['goal-reached']}>
                {t('widgets.donations.reached.thanks')}{' '}
                <span className={s['accent']}>
                  {t('widgets.donations.reached.helped')}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className={s['footer']}>
          {isOwner ? (
            <TextTooltipOnHover
              tooltipText={t!('widgets.donations.button.tooltip')}
              renderReference={({ ref }) => this.renderDonatePointsButton(ref)}
            />
          ) : (
            this.renderDonatePointsButton()
          )}
        </div>
      </>
    );
  }

  renderDonatePointsButton = (ref?: RefHandler) => {
    const { t, isOwner, userUuid } = this.props;
    return (
      <BiLink
        onClick={this.handleDonatePointsClick}
        className={classnames(s['donate-button'], isOwner && s['disabled'])}
        biData={{
          evid: BiEventType.DONATE_POINTS_CLICK,
          recipientid: userUuid,
        }}
      >
        <span ref={ref}>{t('widgets.donations.button.donate')}</span>
      </BiLink>
    );
  };

  handleDonatePointsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const { isOwner, username, pushDonateModal, t } = this.props;
    if (isOwner) {
      return;
    }
    pushDonateModal(username, t!('widgets.donations.donateModal.title'));
  };
}

export default withTranslation()(Donations);
